import { useEffect, useMemo, useState } from 'react';
import type { ClaimSummary, QueueGroup } from '../types';
import { GROUP_LABEL, GROUP_ORDER, TAG_LABEL, compareSummaries, formatMoney, matchesQuery } from '../utils';
import { TagBadge } from './common';
import { LoadMore } from '@/components/lists';
import { isPlainClick } from '@/components/plainClick';

interface QueueProps {
  queue: ClaimSummary[];
  selectedId?: string;
  onSelect: (id: string) => void;
}

/** First 10 of the Ready group, then "Show 50 more" (00-ds-09 §2, rcm-02). */
const READY_FIRST = 10;
const READY_STEP = 50;

/** Near a filing limit first, then oldest visit, then larger amount (D237). */
function sortGroup(rows: ClaimSummary[]): ClaimSummary[] {
  return [...rows].sort(compareSummaries);
}

function Row({ row, selected, onSelect }: { row: ClaimSummary; selected: boolean; onSelect: (id: string) => void }) {
  const a11y = `${row.patient}, ${formatMoney(row.billed)}, ${row.payerShort}, ${row.codes.join(', ')}, ${TAG_LABEL[row.tag]}: ${row.listLine}${row.fileBy ? `, ${row.fileBy.label}` : ''}`;
  return (
    <a
      href={`/claims/${row.id}`}
      className={`clm-qrow${selected ? ' clm-qsel' : ''}`}
      {...(selected ? { 'aria-current': 'true' as const } : {})}
      onClick={(e) => {
        if (!isPlainClick(e)) return;
        e.preventDefault();
        onSelect(row.id);
      }}
    >
      <span className="clm-visually-hidden">{a11y}</span>
      <div className="clm-qtop" aria-hidden="true">
        <span className="clm-qname">{row.patient}</span>
        <span className="clm-qbilled num">{formatMoney(row.billed)}</span>
      </div>
      <div className="clm-qsub" aria-hidden="true">
        {row.payerShort} · {row.codes.join(', ')}
      </div>
      <div className="clm-qtagline" aria-hidden="true">
        <TagBadge tag={row.tag} />
        <span>{row.listLine}</span>
      </div>
      {row.fileBy ? (
        <div className="clm-qfile" aria-hidden="true">
          {row.fileBy.label}
        </div>
      ) : null}
    </a>
  );
}

export function Queue({ queue, selectedId, onSelect }: QueueProps) {
  const [query, setQuery] = useState('');
  const [readyLoaded, setReadyLoaded] = useState(READY_FIRST);
  const [holdOpen, setHoldOpen] = useState(false);

  const searching = query.trim().length > 0;

  // A new search resets the Ready page back to the first window.
  useEffect(() => {
    setReadyLoaded(READY_FIRST);
  }, [query]);

  const byGroup = useMemo(() => {
    const filtered = queue.filter((r) => matchesQuery(r, query));
    const map = new Map<QueueGroup, ClaimSummary[]>();
    for (const g of GROUP_ORDER) map.set(g, []);
    for (const r of filtered) map.get(r.group)?.push(r);
    return map;
  }, [queue, query]);

  const totalMatches = Array.from(byGroup.values()).reduce((n, rows) => n + rows.length, 0);

  return (
    <aside className="clm-queue" aria-label="Claims to review">
      <div className="clm-search-wrap">
        <label className="clm-visually-hidden" htmlFor="clm-search">
          Search claims
        </label>
        <input
          id="clm-search"
          className="clm-search"
          type="text"
          placeholder="Search patient, claim number, payer or amount"
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
        />
      </div>

      {searching && totalMatches === 0 ? (
        <div className="clm-noresults">
          No claims match “{query.trim()}”.{' '}
          <button type="button" className="clm-train-link" onClick={() => { setQuery(''); }}>
            Clear search
          </button>
        </div>
      ) : null}

      {GROUP_ORDER.map((g) => {
        const rows = sortGroup(byGroup.get(g) ?? []);
        if (rows.length === 0) return null;

        if (g === 'on_hold' && !searching) {
          return (
            <section key={g}>
              <button
                type="button"
                className="clm-group-toggle"
                aria-expanded={holdOpen}
                onClick={() => { setHoldOpen((v) => !v); }}
              >
                <span>
                  {GROUP_LABEL[g]} · {rows.length}
                </span>
                <span aria-hidden="true">{holdOpen ? '▾' : '▸'}</span>
              </button>
              {holdOpen
                ? rows.map((r) => <Row key={r.id} row={r} selected={r.id === selectedId} onSelect={onSelect} />)
                : null}
            </section>
          );
        }

        // "Needs your decision" shows every item; "Ready to confirm" loads the
        // first page then "Show 50 more". Search shows all matches in a group.
        const paged = g === 'ready' && !searching;
        const visible = paged ? rows.slice(0, readyLoaded) : rows;

        return (
          <section key={g}>
            <h2 className={`clm-group-head${g === 'needs_decision' ? ' clm-group-needs' : ''}`}>
              {GROUP_LABEL[g]} · {rows.length}
            </h2>
            {visible.map((r) => (
              <Row key={r.id} row={r} selected={r.id === selectedId} onSelect={onSelect} />
            ))}
            {paged ? (
              <LoadMore
                loaded={visible.length}
                total={rows.length}
                step={READY_STEP}
                onMore={() => { setReadyLoaded((n) => n + READY_STEP); }}
              />
            ) : null}
          </section>
        );
      })}
    </aside>
  );
}
